// © Microsoft Corporation. All rights reserved.

import { getPointerCoordinatesByEvent, isPointerOnElement, preventSelection } from "./utils/EventHelpers";
import { lockTouch, unlockTouch } from "./utils/BridgeHelpers";

import { enums } from "@msnews/social-data-service";

/**
 * GENERIC TOUCH & SLIDE HANDLER
 */
interface TouchSlideCallbacks {
    onLongpress?: (target: HTMLElement) => void; // called when longpress mode activated (timeout is achieved)
    onLongpressEnd?: (e: Event) => void; // called at the first touch-end or touch-move after longpress mode activated
    onTouchStart?: (e: Event) => void;
    onTouchEnd?: (e: Event) => void;
    onTriggerClick?: (e: Event) => void;
    onSlide?: (coords: any, e: Event) => void;
    onSlideStart?: (target: HTMLElement) => void;
    onSlideEnd?: () => void;
}

interface TouchSlideConfig {
    slideDisabled?: boolean;
    longpressDisabled?: boolean;
}

class TouchSlideHandler {
    static readonly trackingPeriod = 16; // 60 Hz
    private static lastTouchEndTime = 0;

    // Configurable Parameters
    private longPressTimeout = 250; // [ms]
    private reopenTimeout = 50; // [ms] time period that must pass before another reactionmenu can open by touch
    private slideDisabled = false;
    private longpressDisabled = false;

    // Main Proporties
    private hasLongpressed: boolean;
    private isHandled: boolean; // true if event handlers are attached and not removed
    protected triggerElement: HTMLElement;
    private callbacks: TouchSlideCallbacks;

    constructor(callbacks: TouchSlideCallbacks, config?: TouchSlideConfig) {
        this.callbacks = callbacks;
        this.triggerTouchStartHandler = this.triggerTouchStartHandler.bind(this);
        this.triggerClickHandler = this.triggerClickHandler.bind(this);
        this.touchEndHandler = this.touchEndHandler.bind(this);

        this.slideDisabled = config && config.slideDisabled;
        this.longpressDisabled = config && config.longpressDisabled;
    }

    public setupTrigger(target: HTMLElement) {
        // Event captured from a NEW or DIFFERENT element
        if (this.triggerElement != target) {
            this.unregisterTriggerEventHandlers();
            this.triggerElement = target;
        }
        this.registerTriggerEventHandlers();
    }

    public setupTriggerAtTouchStart(e: Event) {
        this.setupTrigger(e.currentTarget as HTMLElement);
        this.triggerTouchStartHandler(e);
    }

    public dispose() {
        this.unregisterTriggerEventHandlers();
        this.triggerElement = null;
    }

    public releaseTouchEvents() {
        unlockTouch();
        this.unregisterTriggerEventHandlers();
    }

    // :: TOUCH HANDLERS
    private triggerTouchStartHandler(e: Event) {
        !this.longpressDisabled && this.listenLongpress(e.currentTarget as HTMLElement);
        !this.slideDisabled && this.listenSlideStart(e.currentTarget as HTMLElement);
        this.callbacks.onTouchStart && this.callbacks.onTouchStart(e);
        preventSelection(e);
        lockTouch();
        if (!this.enoughTimePassedSinceLastTouchEnd(e.timeStamp)) { return; }
    }

    private triggerClickHandler(e: Event) {
        // If long press activated right before this click
        if (this.hasLongpressed && isPointerOnElement(e, this.triggerElement)) {
            e.stopPropagation();
            return;
        }

        // Check if the event fired when touch point inside on trigger element
        if (isPointerOnElement(e, this.triggerElement)) {
            this.triggerElement.click();
            this.triggerElement.blur();
            unlockTouch();
            this.callbacks.onTriggerClick && this.callbacks.onTriggerClick(e);
        }
    }

    private touchEndHandler(e: Event) {
        this.callbacks.onTouchEnd && this.callbacks.onTouchEnd(e);
        this.releaseTouchEvents();
    }

    // :: LONGPRESS HANDLERS
    private listenLongpress = (target: HTMLElement) => {
        if (this.hasLongpressed) { return; } // if already in lonpress mode do nothing
        let isInterrupted = false;

        // Define functions to call for when timeout achieved or interrupted
        const stopLongpress = () => isInterrupted = true;
        const initiateLongpress = () => {
            if (isInterrupted) { return; }
            this.hasLongpressed = true;
            this.callbacks.onLongpress && this.callbacks.onLongpress(target);

            // Set the event handlers to get out of longpress mode
            document.addEventListener("touchstart", () => {
                this.hasLongpressed = false;
                document.addEventListener("touchend", this.callbacks.onLongpressEnd, {once: true});
                document.addEventListener("touchmove", this.callbacks.onLongpressEnd, {once: true});
            }, {once: true});
        };

        // Set LongPress timer to initiate SLIDE event
        setTimeout(initiateLongpress, this.longPressTimeout);

        // Stop longpress timer if touchend fires before timeout
        target.addEventListener("touchend", stopLongpress, {once: true});
        target.addEventListener("touchmove", stopLongpress, {once: true});

    };

    // :: SLIDE HANDLERS
    private listenSlideStart = (target: HTMLElement) => {
        // Slide Event Initiation
        const initiateSlide = () => {
            this.triggerElement.removeEventListener("touchend", this.triggerClickHandler);

            this.callbacks.onSlideStart && this.callbacks.onSlideStart(target);
            this.listenSliding(); // Start listening slide events
        };

        // Listen TouchMove to initiate SLIDE event
        document.addEventListener("touchmove", initiateSlide, {once: true});

        // TERMINATE initiating SLIDE event
        document.addEventListener("touchend", () => {
            document.removeEventListener("touchmove", initiateSlide);
        }, {once: true});
    };

    private listenSliding() {
        this.hasLongpressed = false;

        let lastMoveTimeStamp = 0;
        const touchMoveHandler = (e: Event) => {
            e.stopPropagation();
            e.cancelable && e.preventDefault();

            // Skip if touch change period is shorter than [touchTrackingPeriod]
            if (e.timeStamp - lastMoveTimeStamp < TouchSlideHandler.trackingPeriod) { return; }
            lastMoveTimeStamp = e.timeStamp;

            const coords = getPointerCoordinatesByEvent(e); // Get last touch coordinates
            this.callbacks.onSlide && this.callbacks.onSlide(coords, e);
        };

        // Listen Slide Events
        document.addEventListener("touchmove", touchMoveHandler, {capture: true, passive: false});

        // Stop Listening Slide Events
        document.addEventListener("touchend", () => {
            document.removeEventListener("touchmove", touchMoveHandler, {capture: true});
            this.callbacks.onSlideEnd && this.callbacks.onSlideEnd();
        }, {capture: true, once: true});
    }

    // :: ADD / REMOVE HANDLERS of TRIGGER ELEMENT
    // ADD EventListeners if triggerElement is set and handlers are not attached
    private registerTriggerEventHandlers() {
        setTimeout(() => {
            if (!this.triggerElement || this.isHandled) { return; }
            this.isHandled = true;
            this.triggerElement.addEventListener("touchstart", this.triggerTouchStartHandler);
            this.triggerElement.addEventListener("touchend", this.triggerClickHandler);
            document.addEventListener("touchend", this.touchEndHandler);
        }, 10);
    }

    // REMOVE EventListeners if triggerElement is set and handlers are attached
    private unregisterTriggerEventHandlers() {
        if (!this.triggerElement || !this.isHandled) { return; }
        this.isHandled = false;
        this.triggerElement.removeEventListener("touchstart", this.triggerTouchStartHandler);
        this.triggerElement.removeEventListener("touchend", this.triggerClickHandler);
        document.removeEventListener("touchend", this.touchEndHandler);
    }

    // UTIL
    private enoughTimePassedSinceLastTouchEnd(timeStamp: number) {
        return timeStamp - TouchSlideHandler.lastTouchEndTime > this.reopenTimeout;
    }
}

/**
 * REACTIONMENU TOUCH & SLIDE HANDLER
 */
interface ReactionMenuTouchSlideCallbacks{
    onEmojiFocus: (el: HTMLElement) => void;
    onEmojiBlur: (el: HTMLElement) => void;
}
class ReactionMenuTouchSlideHandler extends TouchSlideHandler {
    readonly virtualTray = {
        height: 140,
        width: 100,
        hMargin: 20
    };

    // Main Properties
    private containerElement: HTMLElement;
    private emojiElementSelector: string;

    // Variables that change onSlide
    private touchFocusedEmojiElement: HTMLElement;

    // Focus / Blur callbacks
    private reactioMenuCallbacks: ReactionMenuTouchSlideCallbacks;

    constructor(callbacks: TouchSlideCallbacks, reactioMenuCallbacks: ReactionMenuTouchSlideCallbacks, emojiSelector: string, config?: TouchSlideConfig) {
        super({
            ...callbacks,
            onSlide: (coords: any, e: Event) => {
                this.reactionMenuSlideHandler(coords, e);
                callbacks.onSlide && callbacks.onSlide(coords, e);
            },
            onSlideEnd: () => {
                this.reactionHandler();
                callbacks.onSlideEnd && callbacks.onSlideEnd();
            },
            onLongpressEnd: (e: Event) => {
                if (isPointerOnElement(e, this.containerElement) || isPointerOnElement(e, this.triggerElement)) { return; }

                callbacks.onLongpressEnd && callbacks.onLongpressEnd(e);
            }
        }, config);
        this.reactioMenuCallbacks = reactioMenuCallbacks;
        this.emojiElementSelector = emojiSelector;
    }

    public setReactionMenuContainer(el: HTMLElement) {
        this.containerElement = el;
    }

    public dispose() {
        this.containerElement = null;
        super.dispose();
    }

    // :: MAIN HANDLERS
    private reactionHandler() {
        this.touchFocusedEmojiElement && this.touchFocusedEmojiElement.click();
    }
    private reactionMenuSlideHandler(coords: any, e: Event) {
        if (!this.containerElement) { return; }

        const reactionMenuRect: ClientRect = this.containerElement.getBoundingClientRect();

        // Calculate virtual tray ranges based on reactionmenu rect
        const yRange = {start: reactionMenuRect.top - this.virtualTray.height/2, end: reactionMenuRect.bottom + this.virtualTray.height/2};
        const xRange = {start: reactionMenuRect.left, end: reactionMenuRect.left + reactionMenuRect.width};

        // Calculate boundaries per emoji
        const virtualWidthPerEmoji = (xRange.end - xRange.start) / enums.reactionOrder.length;
        const matchingEmojiIndex = Math.min(Math.max(Math.floor((coords.x - xRange.start)/virtualWidthPerEmoji), 0), enums.reactionOrder.length - 1);

        // Check if touch point is inside any of the emoji boundaries then focus or blur
        if (coords.x < xRange.start - this.virtualTray.hMargin || coords.x > xRange.end + this.virtualTray.hMargin || coords.y < yRange.start || coords.y > yRange.end) {
            // Unselect focused emojis when touch leaves the virtual tray
            this.touchFocusedEmojiElement && this.reactioMenuCallbacks.onEmojiBlur(this.touchFocusedEmojiElement);
            this.touchFocusedEmojiElement = null;
        } else {
            // Focus on the emoji matching with the position inside the virtual tray
            const emojiToFocus = this.containerElement.querySelectorAll(this.emojiElementSelector)[matchingEmojiIndex];
            if (this.touchFocusedEmojiElement != emojiToFocus) {
                this.touchFocusedEmojiElement && this.reactioMenuCallbacks.onEmojiBlur(this.touchFocusedEmojiElement);
                this.touchFocusedEmojiElement = emojiToFocus as HTMLElement;
                this.reactioMenuCallbacks.onEmojiFocus(this.touchFocusedEmojiElement);
            }
        }
    }
}

export { TouchSlideHandler, ReactionMenuTouchSlideHandler };